import {areSetsEqual, mapObject, objectFromEntries, pick, range} from '@benchling/js-utils';
import produce from 'immer';
import isEqual from 'react-fast-compare';
import {setup, assign, assertEvent} from 'xstate';

import {assert} from 'Asserts';
import {DEFAULT_MEASUREMENT} from 'constants/MeasurementConstants';
import {generateUniqueName} from 'notebook/util/GenerateUniqueName';
import {getAutopopulatedMaterialInputIdMap} from 'samples/plate-maps/AnnotateFillPlateUtil';
import {
  ConcentrationLayerData,
  ConcentrationLayerMachine,
  createConcentrationLayerId,
  createDefaultLayerSettings,
} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  BioentityContent,
  LiveSchematizedDisplayLayerMetadata,
  PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS,
  PlateMap,
  PreviewSourceError,
  ReagentType,
  Source,
  WellAssignmentMethod,
} from 'samples/plate-maps/PlateMapConstants';
import {
  applyEditsToAllWells,
  applyEditToSelected,
  getColor,
  getGroupIdsForSource,
  getInitialDataParameterTags,
  getInitialSelectedDataParameters,
  getSourceDilutionIdentifier,
} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {updateReagentMeasurementCellsBySchemas} from 'samples/plate-maps/transfer-table/SummaryViewTransferTableUtil';
import {getDefaultMeasurementValidationErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {updateReagentMeasurementCellsByWells} from 'samples/plate-maps/transfer-table/WellViewTransferTableUtil';
import {getInitialTags} from 'schemas/tags/TagUtil';
import {omit} from 'util/omitKey';

import {
  FillPlateModalReducerAction,
  FillPlateModalState,
  FIELD_NAMES_TO_RETAIN_ON_REINITIALIZE,
  updateDependentState,
  DEFAULT_FILL_PLATE_MODAL_STATE,
  FillPlateModalReducerActionType,
} from './FillPlateModalConstants';

type FillPlateModalContext = FillPlateModalState;

export type PlateModalMachineEvents =
  | FillPlateModalReducerAction
  | {type: 'addConcentrationLayer'}
  | {
      type: 'removeConcentrationLayerFromPlateMap';
      layerId: string;
    }
  | {
      type: 'applyPreviewPlateMap';
      plateMap: PlateMap;
    };

/**
 * Finite State Machine for the Fill Plate Modal
 * Currently this is just acting like a simple reducer as the machine
 * only has one state with no events or transitions.
 *
 * Further migration into a full fledged state machine is coming soon.
 * https://benchling.atlassian.net/wiki/spaces/IR/pages/1241546866/Concentration+Layers+for+Plate+Maps
 *
 * Reach out to @kerry.gould on slack before making changes
 */
export const PlateModalMachine = setup({
  types: {
    input: {} as FillPlateModalContext,
    context: {} as FillPlateModalContext,
    events: {} as PlateModalMachineEvents,
  },
  actions: {
    initPlateMap: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.INITIALIZE_PLATE_MAP);
      const initialState =
        context.plateMap == null
          ? context
          : {
              ...DEFAULT_FILL_PLATE_MODAL_STATE,
              // Retain from context only those fields that are not designated for clobbering.
              ...pick(context, ...FIELD_NAMES_TO_RETAIN_ON_REINITIALIZE),
            };
      context.concentrationLayerMachine?.send({
        type: 'initLayers',
        layers: getInitialConcentrationLayers(event.plateMap),
      });
      return updateDependentState({
        ...initialState,
        plateMap: event.plateMap,
        previewPlateMap: initialState.previewPlateMap ?? event.plateMap,
        reagentGroupsById:
          Object.keys(initialState.reagentGroupsById).length === 0
            ? event.assignmentGroups
            : initialState.reagentGroupsById,
      });
    }),

    initProcedureData: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.INITIALIZE_PROCEDURE_DATA);

      const selectedPlateDataParameterIds = event.calculateInitialDataParameters
        ? new Set(
            getInitialSelectedDataParameters(event.plateDataParameters).map(
              (parameter) => parameter.versionId
            )
          )
        : context.selectedPlateDataParameterIds;
      const plateDataParametersById = objectFromEntries(
        event.plateDataParameters.map((parameter) => [parameter.versionId, parameter])
      );
      if (isEqual(context.plateDataParametersById, plateDataParametersById)) {
        return {...context, plateMaterialInputsById: event.plateMaterialInputsById};
      }
      return {
        selectedPlateDataParameterIds,
        plateDataParametersById,
        plateMaterialInputsById: event.plateMaterialInputsById,
      };
    }),

    setInitialSelectedWellSchemaFields: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS);

      const possibleSelectedLayers =
        event.initialSelectedLayerIds ?? event.schemaFieldLayers.map(({attribute}) => attribute.key);
      const selectedWellSchemaFieldIds: Set<string> =
        possibleSelectedLayers.length <= PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS
          ? new Set(possibleSelectedLayers)
          : new Set();
      const liveSchemaFields = event.schemaFieldLayers
        .filter(
          (field): field is LiveSchematizedDisplayLayerMetadata => field.outdated != null && !field.outdated
        )
        .map(({attribute}) => attribute.schemaField);
      const liveSchemaFieldsById = objectFromEntries(liveSchemaFields.map((field) => [field.apiId, field]));
      return {selectedWellSchemaFieldIds, liveSchemaFieldsById};
    }),

    addSelectedPlateDataParameter: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.ADD_SELECTED_PLATE_DATA_PARAMETER);

      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      const selectedPlateDataParameterIds = [
        ...context.selectedPlateDataParameterIds,
        event.parameterVersionId,
      ];
      const selectedParameters = selectedPlateDataParameterIds.map(
        (id) => context.plateDataParametersById[id]
      );
      const emptyTags = getInitialDataParameterTags(selectedParameters);
      const newPlateMap = produce(context.plateMap, (draft) => {
        for (const well of draft.wells) {
          well.dataParameterTags = {...emptyTags, ...well.dataParameterTags};
        }
      });
      return {
        plateMap: newPlateMap,
        selectedPlateDataParameterIds: new Set(selectedPlateDataParameterIds),
      };
    }),
    addSelectedWellSchemaField: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.ADD_SELECTED_WELL_SCHEMA_FIELD);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      const selectedWellSchemaFieldIds = [...context.selectedWellSchemaFieldIds, event.schemaFieldId];
      const selectedWellSchemaFields = selectedWellSchemaFieldIds.map(
        (id) => context.liveSchemaFieldsById[id]
      );
      const emptyTags = objectFromEntries(
        getInitialTags(selectedWellSchemaFields).map((tag) => [tag.tagSchemaField.apiId, tag])
      );
      const newPlateMap = produce(context.plateMap, (draft) => {
        for (const well of draft.wells) {
          well.schemaTags = {...emptyTags, ...well.schemaTags};
        }
      });
      return {
        plateMap: newPlateMap,
        selectedWellSchemaFieldIds: new Set(selectedWellSchemaFieldIds),
      };
    }),

    addSourcesAndSchemas: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.ADD_SOURCES_AND_SCHEMAS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      const {schemaDataById, testOrderInfoById, testOrderErrorsBySourceDilutionId} = event;
      let {reagents} = event;
      const existingSourceDilutionIds = new Set(context.previewedSourceDilutionIdOrdering);
      const existingSchemaIds = new Set(Object.keys(context.reagentGroupsById));
      const colorGenerator = getColor(context.reagentGroupsById);
      const testOrderInfo = {...context.plateMap.testOrderDilutionInfoById, ...testOrderInfoById};
      const autopopulatedMaterialInputIdMap = getAutopopulatedMaterialInputIdMap(
        context.plateMaterialInputsById
      );

      // If we're in a procedure, default the material input association to null or the auto populated choice
      // for all bioentity sources and container source contents.
      if (context.isInProcedure) {
        reagents = produce(reagents, (draft) =>
          draft.map((reagent) => {
            if (reagent.type === ReagentType.SOURCE_CONTAINABLE) {
              return {
                ...reagent,
                // Material inputs on bare sources can be set explicitly via planned values, so don't clobber
                // them here.
                materialInput: reagent.materialInput ?? {
                  materialInputId: autopopulatedMaterialInputIdMap[reagent.id] ?? NO_MATERIAL_INPUT_SENTINEL,
                  error: null,
                },
              };
            } else {
              return {
                ...reagent,
                contents: reagent.contents.map((content) => ({
                  ...content,
                  materialInput: {
                    materialInputId:
                      autopopulatedMaterialInputIdMap[content.id] ?? NO_MATERIAL_INPUT_SENTINEL,
                    error: null,
                  },
                })),
              };
            }
          })
        );
      }

      const newReagentsBySourceDilutionId = {
        ...context.previewedSourcesBySourceDilutionId,
        // If a reagent already exists, clobber it; it may have been updated (e.g. schema change) since it
        // was first added. This would happen when a reagent is added by worklist after having previously
        // been added by search.
        ...objectFromEntries(
          reagents.map((reagent) => [getSourceDilutionIdentifier(reagent, testOrderInfo), reagent])
        ),
      };
      const newSourceDilutionIdOrdering = [
        ...context.previewedSourceDilutionIdOrdering,
        // Don't change a reagent's position in the ordering if it already exists.
        ...reagents
          .map((reagent) => getSourceDilutionIdentifier(reagent, testOrderInfo))
          .filter((sourceDilutionId) => !existingSourceDilutionIds.has(sourceDilutionId)),
      ];
      const newReagentGroupsById = {
        ...context.reagentGroupsById,
        ...objectFromEntries(
          Object.keys(schemaDataById)
            // Don't clobber a schema's color if it already exists.
            .filter((id) => !existingSchemaIds.has(id))
            .map((id) => [
              id,
              {
                id,
                name: schemaDataById[id].name,
                color: colorGenerator.next().value,
                value: {id, iconId: schemaDataById[id].iconId},
              },
            ])
        ),
      };
      return {
        // ...context,
        previewedSourcesBySourceDilutionId: newReagentsBySourceDilutionId,
        previewedSourceDilutionIdOrdering: newSourceDilutionIdOrdering,
        previewedSourceErrorsBySourceDilutionId: {
          ...context.previewedSourceErrorsBySourceDilutionId,
          [PreviewSourceError.INVALID_TRIAL_SAMPLE]: {
            ...context.previewedSourceErrorsBySourceDilutionId[PreviewSourceError.INVALID_TRIAL_SAMPLE],
            ...testOrderErrorsBySourceDilutionId,
          },
        },
        reagentGroupsById: newReagentGroupsById,
        plateMap: {
          ...context.plateMap,
          testOrderDilutionInfoById: testOrderInfo,
        },
      };
    }),

    applySources: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.APPLY_SOURCES);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      const newPlateMap = {
        ...context.previewPlateMap,
        wells: context.previewPlateMap.wells.map((well) => ({
          ...well,
          sources: well.sources.map((source) => omit(source, 'label') as Source),
        })),
      };
      return {
        previewPlateMap: newPlateMap,
        plateMap: newPlateMap,
        previewedSourcesBySourceDilutionId: {},
        previewedSourceDilutionIdOrdering: [],
        previewedSourceErrorsBySourceDilutionId: {[PreviewSourceError.INVALID_TRIAL_SAMPLE]: {}},
        selectedWellIndices: new Set(),
      };
    }),
    removeAllAppliedSourceGroups: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_ALL_APPLIED_SOURCE_GROUPS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      let newPlateMap: PlateMap;
      if (event.clearAllWells) {
        newPlateMap = applyEditsToAllWells(context.plateMap, {sources: []});
      } else {
        newPlateMap = applyEditToSelected(context.plateMap, {sources: []}, context.selectedWellIndices);
      }
      return {
        plateMap: newPlateMap,
      };
    }),

    removeAppliedSourceGroups: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_APPLIED_SOURCE_GROUPS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      const indices = event.clearAllWells
        ? range(0, context.plateMap.wells.length - 1)
        : [...context.selectedWellIndices];
      const newPlateMap = produce(context.plateMap, (draft) => {
        for (const index of indices) {
          const well = draft.wells[index];
          well.sources = well.sources.filter((source) => {
            const wellSourceReagentGroupIds = getGroupIdsForSource(source);
            return !areSetsEqual(wellSourceReagentGroupIds, event.reagentGroupIds);
          });
        }
      });
      return {
        plateMap: newPlateMap,
      };
    }),

    removeContentGroups: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_CONTENT_GROUPS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      const indices = event.clearAllWells
        ? range(0, context.plateMap.wells.length - 1)
        : [...context.selectedWellIndices];

      const newPlateMap = produce(context.plateMap, (draft) => {
        for (const index of indices) {
          const well = draft.wells[index];
          const {contents, removedContents} = well.contents.reduce<{
            contents: Array<BioentityContent>;
            removedContents: Array<BioentityContent>;
          }>(
            (acc, content) => {
              if (event.contentGroupIds.has(content.groupId)) {
                acc.removedContents.push(content);
                return acc;
              }
              acc.contents.push(content);
              return acc;
            },
            {contents: [], removedContents: well.removedContents ?? []}
          );
          well.removedContents = removedContents;
          well.contents = contents;
        }
      });
      return {
        plateMap: newPlateMap,
      };
    }),

    removeSources: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_SOURCES);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      const newPlateMap = produce(context.plateMap, (draft) => {
        for (const well of draft.wells) {
          well.sources = well.sources.filter((source) => !event.sourceIds.has(source.id));
        }
      });
      return {plateMap: newPlateMap};
    }),

    removePreviewedSources: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_PREVIEWED_SOURCES);

      return {
        previewedSourcesBySourceDilutionId: omit(
          context.previewedSourcesBySourceDilutionId,
          ...event.sourceDilutionIds
        ),
        previewedSourceDilutionIdOrdering: context.previewedSourceDilutionIdOrdering.filter(
          (sourceDilutionId) => !event.sourceDilutionIds.includes(sourceDilutionId)
        ),
        previewedSourceErrorsBySourceDilutionId: mapObject(
          context.previewedSourceErrorsBySourceDilutionId,
          (errorsOfType) => omit(errorsOfType, ...event.sourceDilutionIds)
        ) as {
          [errorType in PreviewSourceError]: {[sourceDilutionId: string]: string};
        },
      };
    }),

    removeSelectedPlateDataParameter: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_SELECTED_PLATE_DATA_PARAMETER);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      const parameter = context.plateDataParametersById[event.parameterVersionId];
      return {
        selectedPlateDataParameterIds: new Set(
          [...context.selectedPlateDataParameterIds].filter((id) => id !== event.parameterVersionId)
        ),
        plateMap: produce(context.plateMap, (draft) => {
          for (const well of draft.wells) {
            delete well.dataParameterTags[parameter.versionId];
          }
        }),
      };
    }),
    // This doesn't work with newly added fields when editing a plate design, because tagsToRevertTo is bad
    // Also probably want to make it handle a schema field layer that doesn't have one? In the case of archived or whatever
    removeSelectedWellSchemaField: assign(({context, event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REMOVE_SELECTED_WELL_SCHEMA_FIELD);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      return {
        selectedWellSchemaFieldIds: new Set(
          [...context.selectedWellSchemaFieldIds].filter((id) => id !== event.schemaFieldId)
        ),
        plateMap: produce(context.plateMap, (draft) => {
          for (const [index, well] of draft.wells.entries()) {
            if (event.tagsToRevertTo != null) {
              well.schemaTags[event.schemaFieldId] = event.tagsToRevertTo[index];
            } else {
              delete well.schemaTags[event.schemaFieldId];
            }
          }
        }),
      };
    }),
    reorderSources: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.REORDER_SOURCES);
      return {
        previewedSourceDilutionIdOrdering: event.sourceDilutionIdOrdering,
      };
    }),

    setAssignByRoleGroups: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_ASSIGN_BY_ROLE_GROUPS);
      return {
        assignByRoleGroups: event.assignByRoleGroups,
      };
    }),

    setPatternParams: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_PATTERN_PARAMS);
      return {
        patternParams: event.patternParams,
      };
    }),
    setSelectedWells: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_SELECTED_WELLS);
      return {
        selectedWellIndices: event.selectedWells,
      };
    }),
    setTotalWellQuantityValue: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_VALUE);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      return {
        plateMap: produce(context.plateMap, (draft) => {
          for (const [wellIndex, well] of draft.wells.entries()) {
            if (context.selectedWellIndices.size > 0 && !context.selectedWellIndices.has(wellIndex)) {
              continue;
            }
            well.finalQuantity = {
              measurement: {
                value: event.totalWellQuantityValue,
                unit: well.finalQuantity?.measurement.unit ?? null,
              },
              errors: getDefaultMeasurementValidationErrors(),
            };
          }
        }),
      };
    }),
    setTotalWellQuantityUnits: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_UNITS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );

      return {
        plateMap: produce(context.plateMap, (draft) => {
          for (const [wellIndex, well] of draft.wells.entries()) {
            if (context.selectedWellIndices.size > 0 && !context.selectedWellIndices.has(wellIndex)) {
              continue;
            }
            well.finalQuantity = {
              measurement: {
                value: well.finalQuantity?.measurement.value ?? null,
                unit: event.totalWellQuantityUnit,
              },
              errors: getDefaultMeasurementValidationErrors(),
            };
          }
        }),
      };
    }),
    setViewMode: assign(({event}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_VIEW_MODE);
      return {
        viewMode: event.viewMode,
      };
    }),
    setWellAssignmentMethod: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.SET_WELL_ASSIGNMENT_METHOD);
      return {
        wellAssignmentMethod: event.wellAssignmentMethod,
        // When switching to pattern assignment, assign by role group by default.
        // If this is not possible, updateDependentState will handle correcting to the correct state.
        assignByRoleGroups:
          event.wellAssignmentMethod === WellAssignmentMethod.PATTERN ? true : context.assignByRoleGroups,
      };
    }),
    updatePaginationOffset: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.UPDATE_PAGINATION_OFFSET);
      return {
        pagination: {
          ...context.pagination,
          offset: event.offset,
        },
      };
    }),

    updatePlateMapAnnotations: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.UPDATE_PLATE_MAP_ANNOTATIONS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      const plateMap = produce(context.plateMap, (draft) => {
        for (const [i, well] of draft.wells.entries()) {
          const {role, schemaTags, dataParameterTags, concentrationLayers} = event.plateMap.wells[i];
          well.role = role;
          well.schemaTags = schemaTags;
          well.dataParameterTags = dataParameterTags;
          well.concentrationLayers = concentrationLayers;
        }
        draft.concentrationLayerInfoById = event.plateMap.concentrationLayerInfoById;
      });
      return {plateMap, selectedWellIndices: new Set()};
    }),
    updateReagentMeasurementsBySchemas: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      return {
        plateMap: updateReagentMeasurementCellsBySchemas(
          context.plateMap,
          context.selectedWellIndices,
          event.reagentMeasurementCellsBySchemas
        ),
      };
    }),
    updateReagentMeasurementsByWells: assign(({event, context}) => {
      assertEvent(event, FillPlateModalReducerActionType.UPDATE_REAGENT_MEASUREMENTS_BY_WELLS);
      assert(
        context.plateMap != null && context.previewPlateMap != null,
        'Expected plate map to be initialized'
      );
      return {
        plateMap: updateReagentMeasurementCellsByWells(
          context.plateMap,
          event.reagentMeasurementCellsByWells
        ),
      };
    }),

    updateDependentState: assign(({context}) => updateDependentState(context)),

    spawnConcentrationLayerMachine: assign(({context, spawn}) => {
      const concentrationLayerMachine = spawn(ConcentrationLayerMachine, {
        input: {initialLayers: getInitialConcentrationLayers(context.plateMap)},
      });
      return {
        concentrationLayerMachine,
      };
    }),

    addConcentrationLayer: assign(({event, context}) => {
      assertEvent(event, 'addConcentrationLayer');
      const concentrationLayerContext = context.concentrationLayerMachine!.getSnapshot();
      const layers = concentrationLayerContext.context.layers;
      const layerNames = Array.from(layers.values()).map((layer) => layer.name);

      const newLayerName = generateUniqueName(layerNames, 'Concentration layer', {
        allowNoSuffix: false,
        addSpaceAfterPrefix: true,
      });
      const newLayerId = createConcentrationLayerId();
      const newLayerInfo = {
        layerId: newLayerId,
        name: newLayerName,
      };

      context.concentrationLayerMachine!.send({
        type: 'addLayer',
        layerId: newLayerId,
        name: newLayerName,
      });
      const newPlateMap = produce(context.plateMap, (draft) => {
        assert(draft != null, 'Expected plate map to be initialized');
        draft.concentrationLayerInfoById = {
          ...draft.concentrationLayerInfoById,
          [newLayerId]: newLayerInfo,
        };
        // Add the layer to all wells' concentrationLayers
        for (const well of draft.wells) {
          if (well.concentrationLayers) {
            well.concentrationLayers[newLayerId] = DEFAULT_MEASUREMENT;
          }
        }
      });
      return {
        plateMap: newPlateMap,
      };
    }),

    removeConcentrationLayerFromPlateMap: assign(({event, context}) => {
      assertEvent(event, 'removeConcentrationLayerFromPlateMap');
      context.concentrationLayerMachine!.send({
        type: 'removeLayer',
        layerId: event.layerId,
      });
      const newPlateMap = produce(context.plateMap, (draft) => {
        assert(draft != null, 'Expected plate map to be initialized');
        draft.concentrationLayerInfoById = omit(draft.concentrationLayerInfoById ?? {}, event.layerId);
        // Remove the layer from all wells' concentrationLayers
        for (const well of draft.wells) {
          if (well.concentrationLayers) {
            well.concentrationLayers = omit(well.concentrationLayers, event.layerId);
          }
        }
      });
      return {
        plateMap: newPlateMap,
      };
    }),

    applyPreviewPlateMap: assign(({event, context}) => {
      assertEvent(event, 'applyPreviewPlateMap');
      return {
        plateMap: {...event.plateMap},
        selectedWellIndices: new Set(),
      };
    }),
  },
}).createMachine({
  context: ({input}) => input,
  initial: 'idle',

  on: {
    INITIALIZE_PLATE_MAP: {
      actions: 'initPlateMap',
    },
    INITIALIZE_PROCEDURE_DATA: {
      actions: 'initProcedureData',
    },
    SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS: {
      actions: 'setInitialSelectedWellSchemaFields',
    },
    ADD_SELECTED_PLATE_DATA_PARAMETER: {
      actions: ['addSelectedPlateDataParameter'],
    },
    ADD_SELECTED_WELL_SCHEMA_FIELD: {
      actions: ['addSelectedWellSchemaField', 'updateDependentState'],
    },
    ADD_SOURCES_AND_SCHEMAS: {
      actions: ['addSourcesAndSchemas', 'updateDependentState'],
    },
    APPLY_SOURCES: {
      actions: ['applySources', 'updateDependentState'],
    },
    REMOVE_ALL_APPLIED_SOURCE_GROUPS: {
      actions: ['removeAllAppliedSourceGroups', 'updateDependentState'],
    },
    REMOVE_APPLIED_SOURCE_GROUPS: {
      actions: ['removeAppliedSourceGroups', 'updateDependentState'],
    },
    REMOVE_CONTENT_GROUPS: {
      actions: ['removeContentGroups', 'updateDependentState'],
    },
    REMOVE_SOURCES: {
      actions: ['removeSources', 'updateDependentState'],
    },
    REMOVE_PREVIEWED_SOURCES: {
      actions: ['removePreviewedSources', 'updateDependentState'],
    },
    REMOVE_SELECTED_PLATE_DATA_PARAMETER: {
      actions: ['removeSelectedPlateDataParameter', 'updateDependentState'],
    },
    REMOVE_SELECTED_WELL_SCHEMA_FIELD: {
      actions: ['removeSelectedWellSchemaField', 'updateDependentState'],
    },
    REORDER_SOURCES: {
      actions: ['reorderSources', 'updateDependentState'],
    },
    SET_ASSIGN_BY_ROLE_GROUPS: {
      actions: ['setAssignByRoleGroups', 'updateDependentState'],
    },
    SET_PATTERN_PARAMS: {
      actions: ['setPatternParams', 'updateDependentState'],
    },
    SET_SELECTED_WELLS: {
      actions: ['setSelectedWells', 'updateDependentState'],
    },
    SET_TOTAL_WELL_QUANTITY_VALUE: {
      actions: ['setTotalWellQuantityValue', 'updateDependentState'],
    },
    SET_TOTAL_WELL_QUANTITY_UNITS: {
      actions: ['setTotalWellQuantityUnits', 'updateDependentState'],
    },
    SET_VIEW_MODE: {
      actions: ['setViewMode', 'updateDependentState'],
    },
    SET_WELL_ASSIGNMENT_METHOD: {
      actions: ['setWellAssignmentMethod', 'updateDependentState'],
    },
    UPDATE_PAGINATION_OFFSET: {
      actions: ['updatePaginationOffset', 'updateDependentState'],
    },
    UPDATE_PLATE_MAP_ANNOTATIONS: {
      actions: ['updatePlateMapAnnotations', 'updateDependentState'],
    },
    UPDATE_REAGENT_MEASUREMENTS_BY_SCHEMAS: {
      actions: ['updateReagentMeasurementsBySchemas', 'updateDependentState'],
    },
    UPDATE_REAGENT_MEASUREMENTS_BY_WELLS: {
      actions: ['updateReagentMeasurementsByWells', 'updateDependentState'],
    },
    addConcentrationLayer: {
      actions: ['addConcentrationLayer', 'updateDependentState'],
    },
    removeConcentrationLayerFromPlateMap: {
      actions: ['removeConcentrationLayerFromPlateMap', 'updateDependentState'],
    },
    applyPreviewPlateMap: {
      actions: ['applyPreviewPlateMap', 'updateDependentState'],
    },
  },
  states: {
    idle: {
      entry: ['spawnConcentrationLayerMachine'],
    },
  },
});

function getInitialConcentrationLayers(plateMap: PlateMap | null): Array<ConcentrationLayerData> {
  return Object.values(plateMap?.concentrationLayerInfoById ?? {}).map((measurementLayer) =>
    createDefaultLayerSettings(measurementLayer.layerId, measurementLayer.name)
  );
}
